// ManagersCell.jsx — compact stacked-avatar cell with a "view all" popup.
//
// Reusable anywhere a record exposes a list of people (managers, admins,
// members…). Keeps table rows tidy by showing a few overlapping avatars
// inline and moving the full roster into a themed, dark-mode-safe modal.
//
// Pass an already-flattened array of user objects
// ({ first_name, last_name, profile_picture_url, colour_code, email,
//    designation, country_directory }):
//
//   <ManagersCell people={row.assigned_admins?.map((a) => a.admin)} />
//   <ManagersCell people={managers} maxVisible={4} title="Assigned Admins" />

import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Users, X, MapPin, Mail } from "lucide-react";
import Avatar from "components/ui/Avatar";

const fullName = (p) =>
  `${p?.first_name || ""} ${p?.last_name || ""}`.trim() || "Unknown";

const initialsOf = (p) =>
  `${p?.first_name?.charAt(0) || ""}${p?.last_name?.charAt(0) || ""}`.toUpperCase();

// A single, fixed-size stacked avatar — one consistent ring, no double
// border, perfectly aligned with sibling chips of the same dimensions.
const StackAvatar = ({ person, size }) => (
  <span
    style={{ width: size, height: size }}
    className="relative inline-flex flex-none items-center justify-center overflow-hidden rounded-full ring-2 ring-surface bg-surface_alt box-content"
  >
    {person?.profile_picture_url ? (
      <img
        src={person.profile_picture_url}
        alt={fullName(person)}
        className="h-full w-full rounded-full object-cover"
      />
    ) : (
      <span
        style={{ backgroundColor: person?.colour_code || "#7158EF" }}
        className="flex h-full w-full items-center justify-center rounded-full text-[10px] font-semibold text-white select-none"
      >
        {initialsOf(person) || "?"}
      </span>
    )}
  </span>
);

const ManagersDialog = ({ people, title, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[2050] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Card */}
      <div className="relative w-full max-w-md bg-surface border border-border rounded-xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.18)]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-primary">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/15 text-white">
              <Users size={16} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">{title}</h3>
              <p className="text-[11px] text-white/70">
                {people.length} {people.length !== 1 ? "people" : "person"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto p-4 space-y-2">
          {people.map((person, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border bg-surface_alt"
            >
              <Avatar user={person} size={36} rounded={true} />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-text_primary truncate">
                  {fullName(person)}
                </div>
                {person?.designation?.designation_name && (
                  <div className="text-[11px] text-text_muted truncate">
                    {person.designation.designation_name}
                  </div>
                )}
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  {person?.email && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-text_secondary">
                      <Mail size={11} className="text-text_muted" />
                      <span className="truncate max-w-[180px]">
                        {person.email}
                      </span>
                    </span>
                  )}
                  {person?.country_directory?.country_name && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-text_secondary">
                      <MapPin size={11} className="text-text_muted" />
                      {person.country_directory.country_name}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

const ManagersCell = ({ people, maxVisible = 4, title = "Managers" }) => {
  const [open, setOpen] = useState(false);
  const list = (Array.isArray(people) ? people : []).filter(Boolean);

  if (list.length === 0) {
    return <span className="text-xs text-text_muted italic">Unassigned</span>;
  }

  const visible = list.slice(0, maxVisible);
  const remaining = list.length - visible.length;
  const avatarSize = 26;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="flex items-center -space-x-2"
        title={list.map(fullName).join(", ")}
      >
        {visible.map((person, idx) => (
          <StackAvatar key={idx} person={person} size={avatarSize} />
        ))}

        {remaining > 0 && (
          <span
            style={{ width: avatarSize, height: avatarSize }}
            className="relative z-10 inline-flex flex-none items-center justify-center rounded-full ring-2 ring-surface bg-primary_tint text-primary text-[10px] font-semibold box-content"
          >
            +{remaining}
          </span>
        )}
      </button>

      {open && (
        <ManagersDialog
          people={list}
          title={title}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export default ManagersCell;
